<script>
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import LogGrokTableActions from '@/components/Log/LogGrokTableActions.vue';
import {
  grokTableHeaders,
  grokTimeFormat,
  sortOrder,
  lastUpdateTimeStamp,
  serviceName,
} from '@/components/Log/log.types';
import { LOGService } from '@/services/api/log.service';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { getRandomUniqueId } from '@/utils/helper-function';
import { formatDate } from '@/utils/time/formatDate';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';

export default {
  components: {
    RexTableCompV2,
    LogGrokTableActions,
    RexTableSortField,
    RexTooltipV3,
  },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapState('logState/logCommonState', ['reloadGrokTable']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapState('globalState/app', ['isFormOpened']),
    headersWithoutAction() {
      return this.headers.filter((header) => header.key !== 'actions');
    },
    dispInfiniteFooter() {
      return this.stopInfiniteScroll && this.items?.length > 18;
    },
    dispScrollFooter() {
      return this.items?.length > 18;
    },
    headers() {
      return grokTableHeaders();
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      pageSize: 30,
      currentPage: 1,
      isAsc: false,
      stopInfiniteScroll: false,
      isError: false,
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', ['setReloadGrokTable']),
    formatTableResp(list) {
      return list.map((item) => {
        if (lastUpdateTimeStamp in item) {
          item[lastUpdateTimeStamp] = formatDate(
            item[lastUpdateTimeStamp],
            this.timezoneoffset,
            grokTimeFormat,
          );
        }
        return {
          _id: `${getRandomUniqueId()}`,
          ...item,
          service_id: item.key.service_id,
          tag_name: item?.key?.tag_name ?? '-',
          pattern: item.attributes.parsing_pattern,
        };
      });
    },
    async getGrokTable() {
      try {
        this.isError = false;
        this.loading = true;
        const { data } = await LOGService.getLogRuleData({
          team_id: this.teamID,
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize,
          order: this.isAsc ? sortOrder.ASC : sortOrder.DESC,
        });
        this.items.push(...this.formatTableResp(data));
        if (data?.length <= 18) {
          this.stopInfiniteScroll = true;
        }
        this.loading = false;
        this.setReloadGrokTable(false);
      } catch (error) {
        this.isError = true;
        console.log(error);
        this.loading = false;
        this.setReloadGrokTable(false);
        this.stopInfiniteScroll = true;
      }
    },
    resetTableData() {
      this.currentPage = 1;
      this.items = [];
      this.stopInfiniteScroll = false;
      this.loading = false;
    },
    toggleSorting() {
      this.isAsc = !this.isAsc;
      this.resetTableData();
      this.getGrokTable();
    },
    getServiceColor(item) {
      return item.key.is_group ? 'rgb(59, 191, 85)' : 'rgb(245, 150, 0)';
    },
  },
  watch: {
    reloadGrokTable(newVal) {
      if (newVal) {
        this.resetTableData();
        this.getGrokTable();
      }
    },
    getDurationTime() {
      if (!this.isFormOpened) {
        this.resetTableData();
        this.getGrokTable();
      }
    },
  },
  created() {
    this.serviceName = serviceName;
    this.getGrokTable();
  },
};
</script>

<template>
  <div>
    <RexTableCompV2
      :headers="headers"
      :items="items"
      :loading="loading"
      :striped="true"
      :enableInfiniteScroll="true"
      :enableScrollFooter="dispScrollFooter"
      @fetchNextPageData="
        () => {
          if (items.length > 0 && !stopInfiniteScroll) {
            currentPage++;
            getGrokTable();
          }
        }
      "
    >
      <template
        v-for="header in headers"
        #[`header-${header.key}`]
        :key="header.key"
      >
        <div>
          <RexTableSortField
            v-if="header.sortable"
            :name="header.name"
            @toggleSort="toggleSorting"
            :isAsc="isAsc"
          />
          <div v-else>
            {{ header.name }}
          </div>
        </div>
      </template>
      <template
        v-for="header in headersWithoutAction"
        #[`body-${header.key}`]="{ item }"
        :key="header.key"
      >
        <RexTooltipV3
          :title="item[header.key] || ''"
          class="grok-table-break-content"
        >
          <div
            class="grok-table-break-text"
            :style="{
              color: header.key === serviceName && getServiceColor(item),
            }"
          >
            {{ item[header.key] }}
          </div>
        </RexTooltipV3>
      </template>
      <template #body-actions="{ item }">
        <LogGrokTableActions :rowDetails="item" />
      </template>
      <template v-if="dispInfiniteFooter" #infinite-footer>
        <div class="log-infi-footer">
          <div v-if="isError">
            {{ $t('log.errorWhileFetchingData') }}
            <button
              class="refresh-btn"
              @click="
                () => {
                  resetTableData();
                  getGrokTable();
                }
              "
            >
              <BaseIcon
                name="refresh"
                height="0.8em"
                width="0.8em"
                class="log-refresh-icon"
              />
            </button>
          </div>
          <div v-else>
            {{ $t('log.availableDataLoadedCompletely') }}
          </div>
        </div>
      </template>
    </RexTableCompV2>
  </div>
</template>

<style lang="scss" scoped>
.grok-table-break-content {
  overflow: hidden;
  white-space: nowrap;
}
.grok-table-break-text {
  overflow: hidden;
  text-overflow: ellipsis;
}
.log-infi-footer {
  @include flex-center;
  color: $font-tertiary;
  padding: 1.25rem 0rem;
}
.refresh-btn {
  border: none;
  background-color: inherit;
}
.log-refresh-icon {
  @include svg-fill-pointer($color-brand-accent);
}
:deep(.sort-asc) {
  margin-bottom: -1.1em;
}
</style>
